import { apiClient } from "../client";

export interface PersonSummary {
  id: number;
  name: string;
  email: string;
  role: string;
  department: string;
  assetAssignedCount: number;
  maintainedAssetCount: number;
  openTaskCount: number;
  overdueTaskCount: number;
  lastActivity: string | null;
}

export interface PersonDetails {
  id: number;
  name: string;
  email: string;
  role: string;
  department: {
    id: number;
    name: string;
  };
  assetAssignedCount: number;
  maintainedAssetCount: number;
  openTaskCount: number;
  overdueTaskCount: number;
  lastActivity: string | null;
}

export interface Task {
  id: number;
  name: string;
  description: string | null;
  assetId: number;
  assignedToId: number;
  siteId: number;
  status: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "OVERDUE";
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  dueDate: string;
  completedAt: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  asset?: {
    id: number;
    name: string;
    assetTagId: string;
  };
  assignedTo?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface Asset {
  id: number;
  assetTagId: string;
  name: string;
  description: string | null;
  brand: string | null;
  model: string | null;
  serialNumber: string | null;
  purchaseDate: string | null;
  currentValue: number | null;
  siteId: number;
  organizationId: number;
  departmentId: number | null;
  locationId: number | null;
  healthStatusId: number | null;
  complianceStatusId: number | null;
  assignedToId: number | null;
  maintainedById: number | null;
  createdAt: string;
  updatedAt: string;
  department?: { id: number; name: string };
  location?: { id: number; name: string };
  healthStatus?: { id: number; name: string };
  complianceStatus?: { id: number; name: string };
}

export interface PersonActivityLog {
  id: number;
  userId: number;
  user: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
    googleId: string | null;
    isVerified: boolean;
    verificationCode: string;
    verificationCodeExpiry: string;
    isWelcomeCompleted: boolean;
    onboardingCompleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
  siteId: number;
  actionType: string;
  description: string | null;
  oldValue: string | null;
  newValue: string | null;
  performedBy: number;
  performer: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
    googleId: string | null;
    isVerified: boolean;
    verificationCode: string;
    verificationCodeExpiry: string;
    isWelcomeCompleted: boolean;
    onboardingCompleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
  createdAt: string;
}

export interface PersonNote {
  id: number;
  userId: number;
  siteId: number;
  title: string;
  content: string;
  documentUrl: string | null;
  fileName: string | null;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  creator?: {
    id: number;
    name: string;
    email: string;
    profilePicture: string | null;
    googleId: string | null;
    isVerified: boolean;
    verificationCode: string;
    verificationCodeExpiry: string;
    isWelcomeCompleted: boolean;
    onboardingCompleted: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface GetPeopleSummaryResponse {
  message: string;
  people: PersonSummary[];
  pagination: PaginationMeta;
}

export interface GetPersonDetailsResponse {
  id: number;
  name: string;
  email: string;
  role: string;
  department: {
    id: number;
    name: string;
  };
  assetAssignedCount: number;
  maintainedAssetCount: number;
  openTaskCount: number;
  overdueTaskCount: number;
  lastActivity: string | null;
}

export interface GetPersonAssetsResponse {
  message: string;
  assets: Asset[];
  pagination: PaginationMeta;
}

export interface GetPersonTasksResponse {
  message: string;
  tasks: Task[];
  pagination: PaginationMeta;
}

export interface GetActivityLogsResponse {
  message: string;
  logs: PersonActivityLog[];
  pagination: PaginationMeta;
}

export interface GetPersonNotesResponse {
  message: string;
  notes: PersonNote[];
  pagination: PaginationMeta;
}

export interface CreateTaskRequest {
  name: string;
  description?: string;
  assetId: number;
  assignedToId: number;
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  dueDate: string;
}

export interface UpdateTaskRequest {
  name?: string;
  description?: string;
  priority?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  dueDate?: string;
  status?: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "OVERDUE";
}

export interface CreatePersonNoteRequest {
  title: string;
  content: string;
  documentUrl?: string;
  fileName?: string;
  category?: string;
  tags?: string;
}

export interface UpdatePersonNoteRequest {
  title?: string;
  content?: string;
  documentUrl?: string;
  fileName?: string;
  category?: string;
  tags?: string;
}

export interface PeopleFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: "name" | "role" | "department" | "lastActive";
  sortOrder?: "ASC" | "DESC";
  fields?: string;
}

export interface TaskFilterParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "OVERDUE";
  priority?: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  assignedToId?: number;
  assetId?: number;
}

export interface ApiError {
  message: string | string[];
  error?: string;
  statusCode?: number;
}

export interface AxiosErrorResponse {
  response?: {
    data: ApiError;
  };
  message: string;
}

export class PeopleService {
  /**
   * Get all people in a site with summary information
   * Includes: name, email, role, department, asset counts, task counts
   */
  static async getPeopleSummary(
    organizationId: number,
    siteId: number,
    params?: PeopleFilterParams,
  ): Promise<GetPeopleSummaryResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.sortBy) queryParams.append("sortBy", params.sortBy);
    if (params?.sortOrder) queryParams.append("sortOrder", params.sortOrder);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/people?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/people`;

    return await apiClient.get<GetPeopleSummaryResponse>(url);
  }

  /**
   * Get detailed information for a specific person
   * Includes all summary data plus department details
   */
  static async getPersonDetails(
    organizationId: number,
    siteId: number,
    personId: number,
  ): Promise<GetPersonDetailsResponse> {
    return await apiClient.get<GetPersonDetailsResponse>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}`,
    );
  }

  /**
   * Get all assets assigned to a specific person
   */
  static async getPersonAssets(
    organizationId: number,
    siteId: number,
    personId: number,
    params?: PeopleFilterParams,
  ): Promise<GetPersonAssetsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.fields) queryParams.append("fields", params.fields);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/people/${personId}/assets?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/people/${personId}/assets`;

    return await apiClient.get<GetPersonAssetsResponse>(url);
  }

  /**
   * Get all tasks assigned to a specific person
   * Supports filtering by status, priority, and search
   */
  static async getPersonTasks(
    organizationId: number,
    siteId: number,
    personId: number,
    params?: TaskFilterParams,
  ): Promise<GetPersonTasksResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);
    if (params?.status) queryParams.append("status", params.status);
    if (params?.priority) queryParams.append("priority", params.priority);
    if (params?.assignedToId)
      queryParams.append("assignedToId", params.assignedToId.toString());
    if (params?.assetId)
      queryParams.append("assetId", params.assetId.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/people/${personId}/tasks?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/people/${personId}/tasks`;

    return await apiClient.get<GetPersonTasksResponse>(url);
  }

  /**
   * Create a new task for a person
   */
  static async createTask(
    organizationId: number,
    siteId: number,
    personId: number,
    request: CreateTaskRequest,
  ): Promise<Task> {
    return await apiClient.post<Task>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/tasks`,
      request,
    );
  }

  /**
   * Update an existing task
   */
  static async updateTask(
    organizationId: number,
    siteId: number,
    personId: number,
    taskId: number,
    request: UpdateTaskRequest,
  ): Promise<Task> {
    return await apiClient.put<Task>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/tasks/${taskId}`,
      request,
    );
  }

  /**
   * Mark a task as complete
   */
  static async completeTask(
    organizationId: number,
    siteId: number,
    personId: number,
    taskId: number,
  ): Promise<Task> {
    return await apiClient.post<Task>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/tasks/${taskId}/complete`,
      {},
    );
  }

  /**
   * Get activity logs for a specific person
   */
  static async getActivityLogs(
    organizationId: number,
    siteId: number,
    personId: number,
    params?: PeopleFilterParams,
  ): Promise<GetActivityLogsResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/people/${personId}/activity-logs?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/people/${personId}/activity-logs`;

    return await apiClient.get<GetActivityLogsResponse>(url);
  }

  /**
   * Get all notes for a specific person with optional search
   * Supports filtering by page, limit, and search (searches title and content)
   */
  static async getPersonNotes(
    organizationId: number,
    siteId: number,
    personId: number,
    params?: PeopleFilterParams,
  ): Promise<GetPersonNotesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append("page", params.page.toString());
    if (params?.limit) queryParams.append("limit", params.limit.toString());
    if (params?.search) queryParams.append("search", params.search);

    const queryString = queryParams.toString();
    const url = queryString
      ? `/organizations/${organizationId}/sites/${siteId}/people/${personId}/notes?${queryString}`
      : `/organizations/${organizationId}/sites/${siteId}/people/${personId}/notes`;

    return await apiClient.get<GetPersonNotesResponse>(url);
  }

  /**
   * Create a new note for a person
   * Supports optional document attachment with documentUrl and fileName
   */
  static async createPersonNote(
    organizationId: number,
    siteId: number,
    personId: number,
    request: CreatePersonNoteRequest,
  ): Promise<PersonNote> {
    return await apiClient.post<PersonNote>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/notes`,
      request,
    );
  }

  /**
   * Update an existing person note
   */
  static async updatePersonNote(
    organizationId: number,
    siteId: number,
    personId: number,
    noteId: number,
    request: UpdatePersonNoteRequest,
  ): Promise<PersonNote> {
    return await apiClient.put<PersonNote>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/notes/${noteId}`,
      request,
    );
  }

  /**
   * Delete a person note
   */
  static async deletePersonNote(
    organizationId: number,
    siteId: number,
    personId: number,
    noteId: number,
  ): Promise<void> {
    return await apiClient.delete<void>(
      `/organizations/${organizationId}/sites/${siteId}/people/${personId}/notes/${noteId}`,
    );
  }
}
